import { useMemo } from 'react';
import { CategoryType } from '../../../services/exports/Constants';
import useMenu from '../../../hooks/menu/useMenu';
import collect from 'collect.js';
import CustomCarousel, { Item } from '../CustomCarousel';
import useScreenType from '../../../hooks/utility/useScreenType';
import { CategoryInterface, ProductInterface } from '../../../services/exports/Interfaces';
import { WithActualPrice } from '../../../providers/MenuProvider';
import MostPopularProduct from './MostPopularProduct';
import classNames from 'classnames';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import classnames from 'classnames';
import useCategoryRestriction from '../../../hooks/menu/useCategoryRestriction';

interface Props {
  className?: string;
  toggleProduct: (product: ProductInterface) => void;
}

const MostPopularProductsCarousel = (props: Props) => {
  const { toggleProduct, className } = props;
  const { allCategories } = useMenu();
  const { isDesktop, isTablet } = useScreenType();
  const { isMobileApp } = useMobileApp();
  const getRestriction = useCategoryRestriction();

  const mostPopularCategory = useMemo<CategoryInterface<WithActualPrice<ProductInterface>>>(
    () => collect(allCategories).where('type', CategoryType.MostPopular).first(),
    [allCategories],
  );

  const restrictedTooltipByProductId = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();

    if (!mostPopularCategory) {
      return map;
    }

    for (const product of mostPopularCategory.products) {
      const { isRestricted, tooltip } = getRestriction(product.category_ids);

      if (isRestricted) {
        map.set(product.id, tooltip);
      }
    }

    return map;
  }, [mostPopularCategory, getRestriction]);

  const items = useMemo<Item[]>(() => {
    if (!mostPopularCategory) return [];

    return collect(mostPopularCategory.products)
      .where('is_available', true)
      .unique('id')
      .map((product, index) => {
        const restrictedTooltip = restrictedTooltipByProductId.get(product.id);

        return {
          data: product,
          render: () => (
            <MostPopularProduct
              product={product}
              index={index}
              toggleProduct={toggleProduct}
              isRestricted={Boolean(restrictedTooltip)}
              restrictedTooltip={restrictedTooltip}
            />
          ),
        };
      })
      .toArray();
  }, [mostPopularCategory, restrictedTooltipByProductId, toggleProduct]);

  const itemsPerSlide = useMemo(() => {
    if (isDesktop) return 5;

    if (isTablet) return 4;

    return 2.1;
  }, [isDesktop, isTablet]);

  if (!mostPopularCategory) return null;

  return (
    <div className={classNames('relative w-full', className)} id={`category-id-${mostPopularCategory.id}`}>
      <CustomCarousel
        items={items}
        itemsPerSlide={itemsPerSlide}
        slideJump={isDesktop ? 3 : 2}
        autoHeight
        gap={isDesktop ? 16 : 8}
        arrowsPosition="top"
        hideArrows={false}
        arrowsClassName={classnames('lg:mr-lg mr-md', { 'mb-sm': isMobileApp })}
        itemsClassName="lg:pl-none pl-md"
        title={!isMobileApp && <h5 className="lg:ml-none ml-md font-semibold">{mostPopularCategory.name}</h5>}
      />
    </div>
  );
};

export default MostPopularProductsCarousel;
